import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill from 'react-quill';
import { normalizeMessageContentHtml } from '../../utils/fileUtils';
import 'react-quill/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];

const STATUS_STYLES = {
  COMPLETED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  PICK_TODAY: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  CANCELLED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const formatStatusLabel = (status) => {
  if (!status) return 'Completed';
  return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const hasMeaningfulHtml = (html) => {
  if (!html || html === '<p><br></p>' || html === '<p></p>') return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim().length > 0;
};

const StatPill = ({ label, value, className = '' }) => (
  <div className={`rounded-xl border px-4 py-3 min-w-0 ${className}`}>
    <p className="text-[10px] font-bold uppercase tracking-wider opacity-75 truncate">{label}</p>
    <p className="text-sm sm:text-base font-bold mt-1 tabular-nums leading-snug break-words">{value}</p>
  </div>
);

const LogoutNowModal = ({
  summary,
  userName,
  onClose,
  onConfirm,
  onRelogin,
  readOnly = false,
}) => {
  const [comment, setComment] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [reloggingIn, setReloggingIn] = useState(false);

  const normalizeCommentForEditor = useCallback((raw) => {
    if (!raw?.trim()) return '';
    if (raw.includes('<') && raw.includes('>')) return raw;
    return `<p>${raw.replace(/\n/g, '<br>')}</p>`;
  }, []);

  useEffect(() => {
    setComment(normalizeCommentForEditor(summary?.comment || ''));
  }, [summary?.comment, normalizeCommentForEditor]);

  if (!summary) return null;

  const productivity = summary.productivityPercent ?? 0;
  const completedTasks = summary.completedTasks || [];
  const taskMinsDone = summary.totalCompletedTaskMinutes ?? 0;
  const taskMinsPlanned = summary.totalPlannedTaskMinutes ?? 0;
  const reminderMinsDone = summary.totalCompletedReminderMinutes ?? 0;
  const reminderMinsPlanned = summary.totalPlannedReminderMinutes ?? 0;
  const workflowMinsDone = summary.totalCompletedWorkflowMinutes ?? 0;
  const workflowMinsPlanned = summary.totalPlannedWorkflowMinutes ?? workflowMinsDone;
  const callMins = summary.totalPlannedCallMinutes ?? summary.totalCompletedCallMinutes ?? 0;
  const totalPlanned = summary.totalPlannedMinutes
    ?? (taskMinsPlanned + reminderMinsPlanned + workflowMinsPlanned + callMins);
  const totalDone = summary.totalCompletedMinutes
    ?? (taskMinsDone + reminderMinsDone + workflowMinsDone + callMins);

  const handleConfirm = async () => {
    if (confirming || readOnly) return;
    setConfirming(true);
    try {
      const payload = hasMeaningfulHtml(comment) ? comment : '';
      if (onConfirm) {
        await onConfirm(payload);
      }
      onClose();
    } catch (err) {
      console.error('Failed to confirm logout:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handleRelogin = async () => {
    if (reloggingIn || !onRelogin) return;
    setReloggingIn(true);
    try {
      await onRelogin();
      onClose();
    } catch (err) {
      console.error('Failed to relogin:', err);
    } finally {
      setReloggingIn(false);
    }
  };

  const readOnlyCommentHtml = normalizeMessageContentHtml(comment);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <style>{`
        .logout-modal-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(244, 63, 94, 0.55) transparent;
        }
        .logout-modal-scroll::-webkit-scrollbar { width: 8px; }
        .logout-modal-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #fb7185, #e11d48);
          border-radius: 999px;
        }
        .logout-comment-editor .ql-container { min-height: 280px; font-size: 14px; }
        .logout-comment-editor .ql-editor { min-height: 280px; }
        .logout-comment-readonly .ql-editor { padding: 0; }
      `}</style>

      <div
        className="relative w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-rose-200/70 dark:border-rose-800 bg-white dark:bg-gray-800"
        role="dialog"
        aria-labelledby="logout-now-title"
      >
        <div className="logout-modal-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-rose-600 via-red-600 to-orange-600 text-white">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <p className="text-rose-50/90 text-xs sm:text-sm font-medium">
              {readOnly ? 'Logout summary' : 'End of day wrap-up'}
              {summary.dateLabel ? ` · ${summary.dateLabel}` : ''}
            </p>
            <h2 id="logout-now-title" className="text-2xl sm:text-3xl font-bold tracking-tight mt-1 pr-8">
              {readOnly ? 'Day summary' : `Logging out, ${userName}`}
            </h2>

            <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
              <StatPill label="Logout Time" value={summary.logoutTime || '—'} className="border-white/25 bg-white/15 text-white" />
              <StatPill label="Total Time Spent" value={summary.totalTimeSpent || '0m'} className="border-white/25 bg-white/15 text-white" />
              <StatPill
                label="Break Time"
                value={summary.totalBreakTime || (summary.totalBreakMinutes ? `${summary.totalBreakMinutes}m` : '0m')}
                className="border-white/25 bg-white/15 text-white"
              />
              <StatPill
                label="Check-in"
                value={summary.checkInTime || '—'}
                className="border-white/25 bg-white/15 text-white"
              />
              <StatPill
                label="Productivity"
                value={`${productivity}%`}
                className="border-white/25 bg-white/15 text-white"
              />
            </div>
            {(summary.breakCount > 0 || summary.workingTime) && (
              <p className="mt-3 text-xs text-rose-50/90">
                {summary.breakCount > 0
                  ? `${summary.breakCount} short break${summary.breakCount === 1 ? '' : 's'} taken`
                  : 'No short breaks'}
                {summary.workingTime ? ` · ${summary.workingTime} working time` : ''}
              </p>
            )}
            {readOnly && summary.canRelogin && (
              <p className="mt-3 text-xs text-rose-50/90">
                Time since logout will count as break when you relogin (within 6 hours of logout).
              </p>
            )}
            {readOnly && summary.alreadyLoggedOut && !summary.canRelogin && (
              <p className="mt-3 text-xs text-rose-50/90">
                More than 6 hours since logout — Relogin is closed. Use Login to start again.
              </p>
            )}
          </div>

          <div className="px-4 sm:px-5 py-4 bg-gradient-to-b from-rose-50/40 to-white dark:from-gray-900/40 dark:to-gray-800">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[340px]">
              {/* Left — completed tasks with status */}
              <section className="flex flex-col min-h-0 rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-blue-100 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-950/40">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-100">
                    Tasks Completed
                  </h3>
                  <p className="text-[11px] text-blue-700/80 dark:text-blue-300/80 mt-0.5">
                    {summary.tasksCompletedCount ?? completedTasks.length} tasks · {taskMinsDone}m done
                    {taskMinsPlanned > 0 ? ` of ${taskMinsPlanned}m planned` : ''}
                  </p>
                </div>
                <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/80 min-h-[280px] max-h-[420px]">
                  {completedTasks.length === 0 ? (
                    <li className="px-4 py-10 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No tasks completed today
                    </li>
                  ) : (
                    completedTasks.map((task) => {
                      const status = task.status || 'COMPLETED';
                      const badgeClass = STATUS_STYLES[status] || STATUS_STYLES.COMPLETED;
                      return (
                        <li key={task.id} className="px-4 py-3 flex items-start gap-3">
                          <span className={`flex-shrink-0 mt-0.5 inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${badgeClass}`}>
                            {formatStatusLabel(status)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                              {task.title}
                            </p>
                            {task.minutes != null && task.minutes > 0 && (
                              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
                                {task.minutes} min estimated
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })
                  )}
                </ul>
              </section>

              {/* Right — rich text comments */}
              <section className="flex flex-col min-h-0 rounded-xl border border-rose-200 dark:border-rose-800 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-rose-100 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/40">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-rose-900 dark:text-rose-100">
                    Comments
                  </h3>
                  <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80 mt-0.5">
                    Notes, blockers, or highlights from your day
                  </p>
                </div>
                <div className="flex-1 p-3 min-h-[280px]">
                  {readOnly ? (
                    <div
                      className="logout-comment-readonly prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 min-h-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 px-3 py-2.5"
                      dangerouslySetInnerHTML={{
                        __html: hasMeaningfulHtml(readOnlyCommentHtml)
                          ? readOnlyCommentHtml
                          : '<p class="text-gray-400">No comments added</p>',
                      }}
                    />
                  ) : (
                    <div className="logout-comment-editor rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-200 [&_.ql-toolbar]:dark:border-gray-700 [&_.ql-container]:border-0">
                      <ReactQuill
                        theme="snow"
                        value={comment}
                        onChange={setComment}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder="Summarize your day, wins, blockers, or handoff notes…"
                      />
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* Productivity breakdown */}
            <section className="mt-4 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-200">
                    Total Productivity
                  </p>
                  <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">
                    Completed ÷ planned (tasks + reminders + workflows + calls)
                  </p>
                </div>
                <span className="text-3xl font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
                  {productivity}%
                </span>
              </div>
              <div className="h-3 rounded-full bg-emerald-200/80 dark:bg-emerald-900/50 overflow-hidden mb-3">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                  style={{ width: `${Math.min(100, productivity)}%` }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-[11px]">
                <div className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2 border border-emerald-100 dark:border-emerald-900/50">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">Tasks</span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/90 ml-1 tabular-nums">
                    {taskMinsDone}m / {taskMinsPlanned}m
                  </span>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2 border border-emerald-100 dark:border-emerald-900/50">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">Reminders</span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/90 ml-1 tabular-nums">
                    {reminderMinsDone}m / {reminderMinsPlanned}m
                    {(summary.completedRemindersCount ?? 0) > 0 && ` (${summary.completedRemindersCount} done)`}
                  </span>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2 border border-emerald-100 dark:border-emerald-900/50">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">Workflows</span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/90 ml-1 tabular-nums">
                    {workflowMinsDone}m / {workflowMinsPlanned}m
                    {(summary.workflowsCompletedCount ?? 0) > 0 && ` (${summary.workflowsCompletedCount} done)`}
                  </span>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2 border border-emerald-100 dark:border-emerald-900/50">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">Calls</span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/90 ml-1 tabular-nums">
                    {callMins}m / {callMins}m
                  </span>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2 border border-emerald-100 dark:border-emerald-900/50">
                  <span className="font-semibold text-emerald-900 dark:text-emerald-100">Total</span>
                  <span className="text-emerald-800/90 dark:text-emerald-200/90 ml-1 tabular-nums">
                    {totalDone}m / {totalPlanned}m
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex-shrink-0 px-4 sm:px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          {readOnly ? (
            summary.canRelogin ? (
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleRelogin}
                  disabled={reloggingIn}
                  className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-3 transition-colors shadow-sm disabled:opacity-70"
                >
                  {reloggingIn ? 'Relogging in…' : 'Relogin'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl text-rose-800 dark:text-rose-100 text-sm font-bold py-3 bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/50 dark:hover:bg-rose-900/70 transition-colors"
              >
                Close
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full rounded-xl text-white text-sm sm:text-base font-bold py-3 bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm disabled:opacity-70"
            >
              {confirming ? 'Logging out…' : 'Confirm Logout'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LogoutNowModal;
